import React, { useCallback, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { Input, Select } from '@st-operations/uikit/src/v2'
import debounce from 'lodash.debounce'

import ValidatedForm from '../validated-form/index.jsx'
import { useEventFeedsContext } from '../../../context/index.jsx'
import CustomSelect from '_components/custom-select/index.jsx'

const body = typeof document !== 'undefined' ? document.querySelector('body') : null

const S3_ACCESS_METHOD_OPTIONS = [
  {
    label: 'Partner IAM Role',
    value: 'partner-iam-role',
  },
  {
    label: 'ShareThis IAM Role',
    value: 'sharethis-iam-role',
  },
  {
    label: 'S3 Access Key',
    value: 's3-access-key',
  },
]

const SHARETHIS_S3_PATH_OPTIONS = [
  {
    label: 's3://sharethis-daas-feeds/feeds/<name> (Feeds)',
    value: 'sharethis-daas-feeds/feeds',
  },
  {
    label: 's3://sharethis-sdf/<name> (Social Data Feeds)',
    value: 'sharethis-sdf',
  },
  {
    label:
      's3://sharethis-trashbin/feeds/<name> (contains a TTL that auto-deletes Feeds)',
    value: 'sharethis-trashbin/feeds',
  },
]

const DestinationConfigurationForm = ({
  requiredErrorDestination,
  requiredErrorBucket,
  requiredErrorDestinationName,
  requiredErrorRemoteS3Path,
  requiredErrorS3AccessKeyId,
  requiredErrorS3SecretAccessKeyId,
  requiredErrorS3AccessMethod,
  requiredErrorPartnerAwsPrinciple,
  requiredErrorCredentialName,
  requiredErrorSharethisRoleArn,
  requiredErrorSharethisIamRoleExternalId,
  requiredErrorEndpoint,
  requiredErrorKey,
  requiredErrorSecret,
  requiredErrorProject,
  requiredErrorRemotePath,
  requiredErrorServiceAccount,
  requiredErrorHost,
  requiredErrorPassword,
  requiredErrorPort,
  requiredErrorSftpRemotePath,
  requiredErrorSftpUsername,
}) => {
  const {
    data: { deliveries },
    schema,
    dispatch,
    ACTIONS,
  } = useEventFeedsContext()
  const delivery = deliveries[0] ?? {}
  const [state, setState] = React.useState(delivery)

  const destinationOptions = useMemo(
    () => schema.deliveries?.['Destination Configuration']?.destination.enum,
    [schema]
  )

  useEffect(() => {
    setState(delivery)
  }, [delivery])

  const handleChangeState = (value, name) => {
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleSelectChangeState = (option, name, value) => {
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const onDeliveryInputChange = useCallback(
    debounce((newState) => {
      dispatch({
        type: ACTIONS.UPDATE_DELIVERY,
        payload: newState,
      })
    }, 500),
    [dispatch, ACTIONS]
  )

  useEffect(() => {
    onDeliveryInputChange(state)
  }, [state])

  const shareThisS3 = state.destination === 'sharethis-s3'
  const partnerS3 = state.destination === 'partner-s3'
  const s3CrossAccount = state.destination === 's3-cross-account'
  const api = state.destination === 'api'
  const gcs = state.destination === 'gcs'
  const sftp = state.destination === 'sftp'
  const accessMethodPartner =
    state.s3AccessMethod === 'partner-iam-role' && (shareThisS3 || partnerS3)
  const accessMethodShareThis =
    state.s3AccessMethod === 'sharethis-iam-role' && (shareThisS3 || partnerS3)
  const accessMethodS3AccessKey = state.s3AccessMethod === 's3-access-key'

  // Limit S3 access method options for S3 Cross Account to only S3 Access Key
  const s3AccessMethodOptions = s3CrossAccount
    ? [
        {
          label: 'S3 Access Key',
          value: 's3-access-key',
        },
      ]
    : S3_ACCESS_METHOD_OPTIONS

  // Ensure that when S3 Cross Account is selected, the access method is set to S3 Access Key
  useEffect(() => {
    if (s3CrossAccount && state.s3AccessMethod !== 's3-access-key') {
      setState((prev) => ({ ...prev, s3AccessMethod: 's3-access-key' }))
    }
  }, [s3CrossAccount])

  return (
    <ValidatedForm
      className="pb-20"
      inputs={[
        {
          label: 'Destination',
          required: requiredErrorDestination,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: true,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Destination
              </span>
              <Select
                name="destination"
                value={state.destination}
                options={destinationOptions}
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorDestination,
                })}
                onChange={handleSelectChangeState}
                isSearchable
                menuPortalTarget={body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    maxHeight: 200,
                  }),
                }}
                useReactSelect
              />
            </>
          ),
        },
        {
          label: 'S3 Access Method',
          required: requiredErrorS3AccessMethod,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: partnerS3 || shareThisS3 || s3CrossAccount,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                S3 Access Method
              </span>
              <Select
                name="s3AccessMethod"
                value={state.s3AccessMethod}
                options={s3AccessMethodOptions}
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorS3AccessMethod,
                })}
                onChange={handleSelectChangeState}
                isSearchable
                menuPortalTarget={body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    maxHeight: 200,
                  }),
                }}
                useReactSelect
              />
            </>
          ),
        },
        {
          label: 'Name',
          required: requiredErrorDestinationName,
          validationClassName: 'delivery-content__required--destination-configuration',
          show:
            accessMethodPartner ||
            accessMethodShareThis ||
            accessMethodS3AccessKey ||
            api ||
            gcs ||
            sftp,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Name
              </span>
              <div className={classNames('delivery-content__content-box')}>
                <Input
                  className={classNames('delivery-content__content-select--name', {
                    'delivery-content__required-field': requiredErrorDestinationName,
                  })}
                  name="name"
                  onChange={handleChangeState}
                  value={state.name}
                  placeholder="your-destination-name"
                />
                <If condition={shareThisS3 || partnerS3}>
                  <span
                    className={classNames(
                      'delivery-content__content-label',
                      'delivery-content__content-label--s3-path',
                      'block text-[12px] mt-2'
                    )}
                  >
                    {`Path to generated files: s3://${state.s3Path || '<bucket>'}/${
                      state.name || '<name>'
                    }/`}
                  </span>
                </If>
              </div>
            </>
          ),
        },
        {
          label: 'S3 Path',
          required: requiredErrorBucket,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: accessMethodPartner || accessMethodShareThis || accessMethodS3AccessKey,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                S3 Path
              </span>
              <div className={classNames('delivery-content__content-box')}>
                <CustomSelect
                  className={classNames('delivery-content__content-select--name', {
                    'delivery-content__required-field': requiredErrorBucket,
                  })}
                  options={SHARETHIS_S3_PATH_OPTIONS}
                  name="s3Path"
                  value={state.s3Path}
                  onChange={(_, name, value) => handleChangeState(value, name)}
                  placeholder="Select a S3 path..."
                  menuPortalTarget={body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      maxHeight: 200,
                    }),
                  }}
                />
              </div>
            </>
          ),
        },
        {
          label: 'Remote S3 Path',
          required: requiredErrorRemoteS3Path,
          validationClassName: 'delivery-content__required--destination-configuration',
          show:
            accessMethodPartner ||
            (accessMethodS3AccessKey && (partnerS3 || s3CrossAccount)),
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Remote S3 Path
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorRemoteS3Path,
                })}
                name="remoteS3Path"
                onChange={handleChangeState}
                value={state.remoteS3Path}
              />
            </>
          ),
        },
        {
          label: 'Partner AWS Principal (User or Role ARN)',
          required: requiredErrorPartnerAwsPrinciple,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: accessMethodPartner || accessMethodShareThis,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Partner AWS Principle (User or Role ARN)
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorPartnerAwsPrinciple,
                })}
                name="partnerAwsPrincipal"
                onChange={handleChangeState}
                value={state.partnerAwsPrincipal}
                placeholder="arn:aws:iam::<AWS_ACCOUNT_ID>:<ROLE/USER>"
              />
            </>
          ),
        },
        {
          label: 'Partner IAM Role External ID (optional)',
          // optional
          validationClassName: 'delivery-content__required--destination-configuration',
          show: accessMethodPartner,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Partner IAM Role External ID (optional)
              </span>
              <Input
                className={classNames('delivery-content__content-select')}
                name="partnerIamRoleExternalId"
                onChange={handleChangeState}
                value={state.partnerIamRoleExternalId}
              />
            </>
          ),
        },
        {
          label: 'Credential Name',
          required: requiredErrorCredentialName,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: accessMethodPartner || accessMethodShareThis,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Credential Name
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorCredentialName,
                })}
                name="credentialName"
                onChange={handleChangeState}
                value={state.credentialName}
              />
            </>
          ),
        },
        {
          label: 'ShareThis Role ARN',
          required: requiredErrorSharethisRoleArn,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: accessMethodPartner || accessMethodShareThis,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                ShareThis Role ARN
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorSharethisRoleArn,
                })}
                name="sharethisRoleArn"
                onChange={handleChangeState}
                value={state.sharethisRoleArn}
              />
            </>
          ),
        },
        {
          label: 'ShareThis IAM Role External ID',
          required: requiredErrorSharethisIamRoleExternalId,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: accessMethodPartner || accessMethodShareThis,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                ShareThis IAM Role External ID
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorSharethisIamRoleExternalId,
                })}
                name="sharethisIamRoleExternalId"
                onChange={handleChangeState}
                value={state.sharethisIamRoleExternalId}
              />
            </>
          ),
        },
        {
          label: 'S3 Access Key ID',
          required: requiredErrorS3AccessKeyId,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: accessMethodS3AccessKey && (partnerS3 || s3CrossAccount),
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                S3 Access Key ID
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorS3AccessKeyId,
                })}
                name="s3AccessKeyId"
                onChange={handleChangeState}
                value={state.s3AccessKeyId}
                disabled={shareThisS3}
              />
            </>
          ),
        },
        {
          label: 'S3 Secret Access Key',
          required: requiredErrorS3SecretAccessKeyId,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: accessMethodS3AccessKey && (partnerS3 || s3CrossAccount),
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                S3 Secret Access Key
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorS3SecretAccessKeyId,
                })}
                name="s3SecretAccessKey"
                onChange={handleChangeState}
                value={state.s3SecretAccessKey}
                disabled={shareThisS3}
              />
            </>
          ),
        },
        {
          label: 'Endpoint',
          required: requiredErrorEndpoint,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: api,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Endpoint
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorEndpoint,
                })}
                name="apiEndpoint"
                onChange={handleChangeState}
                value={state.apiEndpoint}
              />
            </>
          ),
        },
        {
          label: 'Key',
          required: requiredErrorKey,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: api,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Key
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorKey,
                })}
                name="apiKey"
                onChange={handleChangeState}
                value={state.apiKey}
              />
            </>
          ),
        },
        {
          label: 'Secret',
          required: requiredErrorSecret,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: api,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Secret
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorSecret,
                })}
                name="apiSecretKey"
                onChange={handleChangeState}
                value={state.apiSecretKey}
              />
            </>
          ),
        },
        {
          label: 'Project',
          required: requiredErrorProject,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: gcs,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Project
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorProject,
                })}
                name="gcsProject"
                onChange={handleChangeState}
                value={state.gcsProject}
              />
            </>
          ),
        },
        {
          label: 'Remote Path',
          required: requiredErrorRemotePath,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: gcs,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Remote Path
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorRemotePath,
                })}
                name="gcsRemotePath"
                onChange={handleChangeState}
                value={state.gcsRemotePath}
              />
            </>
          ),
        },
        {
          label: 'Service Account',
          required: requiredErrorServiceAccount,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: gcs,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Service Account
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorServiceAccount,
                })}
                name="gcsServiceAccount"
                onChange={handleChangeState}
                value={state.gcsServiceAccount}
              />
            </>
          ),
        },
        {
          label: 'Host',
          required: requiredErrorHost,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: sftp,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Host
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorHost,
                })}
                name="sftpHost"
                onChange={handleChangeState}
                value={state.sftpHost}
              />
            </>
          ),
        },
        {
          label: 'Password',
          required: requiredErrorPassword,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: sftp,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Password
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorPassword,
                })}
                name="sftpPassword"
                onChange={handleChangeState}
                value={state.sftpPassword}
              />
            </>
          ),
        },
        {
          label: 'Port',
          required: requiredErrorPort,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: sftp,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Port
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorPort,
                })}
                name="sftpPort"
                onChange={handleChangeState}
                value={state.sftpPort}
              />
            </>
          ),
        },
        {
          label: 'Remote Path',
          required: requiredErrorSftpRemotePath,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: sftp,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Remote Path
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorSftpRemotePath,
                })}
                name="sftpRemotePath"
                onChange={handleChangeState}
                value={state.sftpRemotePath}
              />
            </>
          ),
        },
        {
          label: 'Username',
          required: requiredErrorSftpUsername,
          validationClassName: 'delivery-content__required--destination-configuration',
          show: sftp,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--destination-configuration'
                )}
              >
                Username
              </span>
              <Input
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorSftpUsername,
                })}
                name="sftpUsername"
                onChange={handleChangeState}
                value={state.sftpUsername}
              />
            </>
          ),
        },
      ]}
    />
  )
}

export default DestinationConfigurationForm
